<template>
  <b-modal
    v-model="showModal"
    :title="`Contribute to ${currentGoal ? currentGoal.title : 'Goal'}`"
    size="lg"
    centered
    hide-footer
    @hidden="handleModalHidden"
  >
    <div v-if="currentGoal" class="contribution-modal">
      <!-- Goal Summary -->
      <div class="goal-summary">
        <div class="goal-progress-info">
          <h5 class="cabin-family semi-bold">{{ currentGoal.title }}</h5>
          <div class="progress-details">
            <span class="current-amount primary cabin-family semi-bold">
              {{ formatCurrency(currentGoal.current_amount || 0) }}
            </span>
            <span class="separator"> / </span>
            <span class="target-amount secondary-text">
              {{ formatCurrency(currentGoal.target_amount || 0) }}
            </span>
          </div>
          <ProgressBar
            :percentage="currentGoal.progress_percentage || 0"
            color="primary"
            show-label
            height="8px"
          />
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="contributionSuccess" class="success-message">
        <div class="success-content">
          <i class="fas fa-check-circle success-icon"></i>
          <div class="success-text">
            <h6 class="cabin-family semi-bold">Contribution Added Successfully!</h6>
            <p class="secondary-text">Your goal progress has been updated.
              You can close this modal to see the updated progress.</p>
          </div>
        </div>
      </div>

      <!-- Contribution Form -->
      <form @submit.prevent="submitContribution">
        <div class="form-group">
          <label for="amount" class="form-label cabin-family semi-bold">
            Contribution Amount *
          </label>
          <CurrencyInput
            v-model="form.amount"
            :has-error="!!errors.amount"
            :error-message="errors.amount"
            placeholder="Enter amount to contribute"
            @error="handleInputError"
          />
        </div>

        <div class="form-group">
          <label for="description" class="form-label cabin-family semi-bold">
            Description (Optional)
          </label>
          <textarea
            v-model="form.description"
            class="form-control"
            :class="{ 'is-invalid': !!errors.description }"
            rows="3"
            placeholder="Add a note about this contribution..."
          />
          <div v-if="errors.description" class="error-message">
            {{ errors.description }}
          </div>
        </div>

        <div class="form-group">
          <label for="contributionDate" class="form-label cabin-family semi-bold">
            Contribution Date
          </label>
          <DatePicker
            v-model="form.contributionDate"
            :has-error="!!errors.contributionDate"
            :error-message="errors.contributionDate"
            :max-date="new Date().toISOString().split('T')[0]"
          />
        </div>

        <!-- Quick Amount Buttons -->
        <div class="quick-amounts">
          <label class="form-label cabin-family semi-bold">Quick Amounts</label>
          <div class="quick-amount-buttons">
            <button
              v-for="amount in quickAmounts"
              :key="amount"
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="setQuickAmount(amount)"
            >
              {{ formatCurrency(amount) }}
            </button>
          </div>
        </div>

        <!-- Contribution Impact -->
        <div v-if="contributionImpact" class="contribution-impact">
          <h6 class="cabin-family semi-bold">Contribution Impact</h6>
          <div class="impact-details">
            <div class="impact-item">
              <span class="label">New Total:</span>
              <span class="value primary cabin-family semi-bold">
                {{ formatCurrency(contributionImpact.newTotal) }}
              </span>
            </div>
            <div class="impact-item">
              <span class="label">Progress:</span>
              <span class="value success cabin-family semi-bold">
                {{ contributionImpact.newProgress.toFixed(1) }}%
              </span>
            </div>
            <div class="impact-item">
              <span class="label">Remaining:</span>
              <span class="value secondary-text">
                {{ formatCurrency(contributionImpact.remaining) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn primary-btn"
            :disabled="loading || !isFormValid"
          >
            <LoadingSpinner
              v-if="loading"
              size="small"
              variant="white"
            />
            <span v-else>Contribute {{ formatCurrency(form.amount || 0) }}</span>
          </button>
        </div>
      </form>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { formatCurrency } from '../../utils/formatters';
import {
  ProgressBar, CurrencyInput, DatePicker, LoadingSpinner,
} from '../shared';

export default {
  name: 'ContributionModal',
  components: {
    ProgressBar,
    CurrencyInput,
    DatePicker,
    LoadingSpinner,
  },
  props: {
    goal: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      loading: false,
      contributionSuccess: false, // Track if contribution was successful
      form: {
        amount: '',
        description: '',
        contributionDate: new Date().toISOString().split('T')[0],
      },
      errors: {},
      quickAmounts: [1000, 5000, 10000, 25000, 50000],
    };
  },
  computed: {
    currentGoal() {
      return this.goal;
    },

    isFormValid() {
      return this.form.amount
             && this.form.amount > 0
             && !this.errors.amount
             && this.form.contributionDate;
    },

    contributionImpact() {
      if (!this.currentGoal || !this.form.amount) return null;

      const currentAmount = this.currentGoal.current_amount || 0;
      const targetAmount = this.currentGoal.target_amount || 0;
      const contributionAmount = parseFloat(this.form.amount) || 0;

      const newTotal = currentAmount + contributionAmount;
      const newProgress = (newTotal / targetAmount) * 100;
      const remaining = Math.max(0, targetAmount - newTotal);

      return {
        newTotal,
        newProgress: Math.min(100, newProgress),
        remaining,
      };
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.showModal = newVal;
      },
    },
    showModal(newVal) {
      this.$emit('update:visible', newVal);
    },
  },
  methods: {
    ...mapActions('goals', ['contributeToGoal']),

    formatCurrency,

    setQuickAmount(amount) {
      this.form.amount = amount;
      this.validateForm();
    },

    handleInputError(error) {
      this.$set(this.errors, 'amount', error);
    },

    validateForm() {
      this.errors = {};

      if (!this.form.amount) {
        this.$set(this.errors, 'amount', 'Contribution amount is required');
      } else if (this.form.amount <= 0) {
        this.$set(this.errors, 'amount', 'Amount must be greater than 0');
      }

      if (!this.form.contributionDate) {
        this.$set(this.errors, 'contributionDate', 'Contribution date is required');
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitContribution() {
      if (!this.validateForm()) return;

      this.loading = true;

      try {
        const contributionData = {
          amount: parseFloat(this.form.amount),
          description: this.form.description,
          contribution_date: this.form.contributionDate,
          paymentMethod: 'wallet',
        };

        const result = await this.contributeToGoal({
          goalId: this.currentGoal.id,
          contribution: contributionData,
        });

        if (result.success) {
          this.$toast.success('Contribution added successfully!');
          // Emit success event - Vuex store has already updated the goal data
          this.$emit('contribution-success', result.data);
          // Show success state briefly then close
          this.showSuccessAndClose();
        } else {
          this.$toast.error(result.error || 'Failed to add contribution');
        }
      } catch (error) {
        console.error('Contribution error:', error);
        this.$toast.error('An error occurred while adding contribution');
      } finally {
        this.loading = false;
      }
    },

    showSuccessAndClose() {
      // Show success state
      this.contributionSuccess = true;
      // Reset form for next contribution
      this.resetForm();
      // Close modal after 2 seconds to show updated data
      setTimeout(() => {
        this.closeModal();
      }, 2000);
    },

    resetForm() {
      this.form = {
        amount: '',
        description: '',
        contributionDate: new Date().toISOString().split('T')[0],
      };
      this.errors = {};
    },

    closeModal() {
      this.showModal = false;
      this.contributionSuccess = false; // Reset success state
    },

    handleModalHidden() {
      this.resetForm();
      this.contributionSuccess = false; // Reset success state
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.contribution-modal {
  .goal-summary {
    background: #F8F9FA;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .success-message {
    background: rgba($success, 0.1);
    border: 1px solid rgba($success, 0.3);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .success-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .success-icon {
    font-size: 20px;
    color: $success;
    flex-shrink: 0;
  }

  .success-text {
    flex: 1;

    h6 {
      margin-bottom: 0.25rem;
      color: $black;
    }

    p {
      margin-bottom: 0;
      font-size: 14px;
    }
  }

  .goal-progress-info h5 {
    margin-bottom: 0.5rem;
    color: $black;
  }

  .progress-details {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    gap: 0.5rem;
  }

  .current-amount {
    font-size: 18px;
  }

  .separator {
    color: $secondary;
  }

  .target-amount {
    font-size: 16px;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    color: $black;
  }

  .error-message {
    color: $primary;
    font-size: 12px;
    margin-top: 0.25rem;
  }

  .quick-amounts {
    margin-bottom: 1rem;

    .form-label {
      margin-bottom: 0.5rem;
    }
  }

  .quick-amount-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .btn {
      border-color: $primary;
      color: $primary;

      &:hover {
        background: $primary;
        color: $white;
      }
    }
  }

  .contribution-impact {
    background: $primary-light;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;

    h6 {
      margin-bottom: 0.75rem;
      color: $black;
    }
  }

  .impact-details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .impact-item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .label {
      font-size: 14px;
      color: $secondary;
    }

    .value {
      font-size: 14px;
    }
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #E0E0E0;

    .btn {
      min-width: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
  }
}

@media (max-width: 576px) {
  .contribution-modal {
    .quick-amount-buttons {
      .btn {
        font-size: 12px;
        padding: 0.25rem 0.5rem;
      }
    }

    .impact-details {
      .impact-item {
        font-size: 13px;
      }
    }

    .modal-actions {
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
